import { ZEN_API_DEV_PROXY_PATH } from '@/config/zen-api-dev-proxy';
import { useAnalyticsDashboardAccess } from '@/hooks/queries/analytics-dashboard';
import { useCanAccessAnalyticsDashboard } from '@/hooks/queries/permissions';
import { LegacyDashboardRedirect } from '@/routes/legacy-dashboard-redirect';
import { AnalyticsDashboardAccess } from '@/services/analytics-dashboard-service';
import { Icon } from '@iconify/react';
import { Button, Card, Spinner } from '@nextui-org/react';
import { AxiosError } from 'axios';

const GENERIC_ERROR = 'Something went wrong while loading the dashboard.';

const NOT_FOUND_MESSAGES: Record<string, string> = {
  no_dashboard: 'No dashboard is available for this tenant yet.',
  no_published_week: 'The dashboard has not been published yet.',
};

const getErrorState = (error: unknown) => {
  const response = (error as AxiosError<{ code?: string }>).response;
  const code = response?.data?.code;

  switch (response?.status) {
    case 403:
      return {
        message: 'You do not have access to the analytics dashboard.',
        canRetry: false,
      };
    case 404:
      return {
        message: (code && NOT_FOUND_MESSAGES[code]) || GENERIC_ERROR,
        canRetry: false,
      };
    case 503:
      return { message: 'Temporarily unavailable.', canRetry: true };
    default:
      // 401 is handled by the API client's logged-out flow.
      return { message: GENERIC_ERROR, canRetry: false };
  }
};

/** Under `vite dev` the iframe goes through the same-origin proxy that carries the session cookie. */
const getIframeSrc = (access: AnalyticsDashboardAccess) =>
  import.meta.env.DEV ? `${ZEN_API_DEV_PROXY_PATH}${access.path}` : access.url;

const DashboardView = () => {
  const { data, error, isError, isFetching, refetch } =
    useAnalyticsDashboardAccess();

  const renderContent = () => {
    if (isError) {
      const { message, canRetry } = getErrorState(error);
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
          <Icon
            icon="lucide:bar-chart-3"
            className="h-12 w-12 text-default-400"
          />
          <p className="text-default-500">{message}</p>
          {canRetry && (
            <Button
              variant="flat"
              onPress={() => refetch()}
              isLoading={isFetching}
            >
              Retry
            </Button>
          )}
        </div>
      );
    }

    if (!data) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      );
    }

    return (
      <iframe
        // A new published week remounts the iframe so it reloads.
        key={data.week}
        title="Analytics dashboard"
        src={getIframeSrc(data)}
        sandbox="allow-scripts allow-same-origin allow-popups"
        allow="clipboard-write"
        className="block h-full w-full flex-1 border-0"
      />
    );
  };

  return (
    // Fills the layout's main area so only the embedded page scrolls. No
    // heading here: the embedded dashboard carries its own title.
    <div className="flex min-h-0 flex-1 flex-col p-2 md:px-6 md:pb-6 md:pt-0">
      <Card
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        shadow="none"
      >
        {renderContent()}
      </Card>
    </div>
  );
};

/**
 * Users without the permission never see this page: /dashboard was Home's old
 * address, so they follow the legacy redirect to /home, keeping query and hash.
 */
const DashboardPage = () => {
  const { hasPermission, isPending } = useCanAccessAnalyticsDashboard();

  if (isPending) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!hasPermission) {
    return <LegacyDashboardRedirect />;
  }

  return <DashboardView />;
};

export default DashboardPage;
